import {
  type GraphPathLookupMulticastParameters,
  type GraphPathLookupMulticastParametersBase,
  isAutomaticFirstHop,
  isUserDefinedFirstHop,
} from '@ipfabric/utils';
import type { Vrf } from 'components/Graph/GraphSidePanel/GraphPathlookup/fetchVrf';
import { isVrfAutodetect } from 'components/Graph/GraphSidePanel/GraphPathlookup/helpers/isVrfAutodetect';
import { PATH_LOOKUP_GROUP_BY } from 'components/Graph/model/pathLookup';
import {
  type GraphAdvancedOptionsFormValues,
  advancedOptionsToFormValues,
  formValuesToAdvancedOptions,
} from './PathLookupAdvancedOptionsFormValues';
import {
  type EntryPointValue,
  entryPointValuesToEntryPoints,
  entryPontsToEntryPointValues,
} from './helpers/entryPoint';

export interface MulticastFormValues extends GraphAdvancedOptionsFormValues {
  firstHopAlgorithm: 'automatic' | 'userDefined';
  group: string;
  receiver: string;
  securedPath: boolean;
  source: string;
  vrf: Vrf | null;
  entryPoints: EntryPointValue[];
}

export const multicastFormValuesToParameters = (
  values: MulticastFormValues,
): GraphPathLookupMulticastParameters => {
  const firstHopAlgorithm =
    values.firstHopAlgorithm === 'automatic'
      ? {
          type: values.firstHopAlgorithm,
          ...(!isVrfAutodetect(values.vrf) && { vrf: values.vrf }),
        }
      : {
          type: values.firstHopAlgorithm,
          entryPoints: entryPointValuesToEntryPoints(values.entryPoints),
        };

  const endpointValues: GraphPathLookupMulticastParametersBase = {
    ...(values.receiver && { receiver: values.receiver }),
    group: values.group,
    groupBy: PATH_LOOKUP_GROUP_BY,
    pathLookupType: 'multicast',
    securedPath: values.securedPath,
    source: values.source,
    type: 'pathLookup',
    firstHopAlgorithm,
  } as const;

  const advancedOptions = {
    enableRegions: values.enableRegions,
    protocol: values.protocol,
    srcRegions: values.srcRegions,
    dstRegions: values.dstRegions,
    ttl: values.ttl,
    fragmentOffset: values.fragmentOffset,
    applications: values.applications,
    applicationsMatchWithRegex: values.applicationsMatchWithRegex,
    category: values.category,
    tracked: values.tracked,
    srcPorts: values.srcPorts,
    dstPorts: values.dstPorts,
    icmpOptions: values.icmpOptions,
    flags: values.flags,
    url: values.url,
  };

  return {
    ...endpointValues,
    ...formValuesToAdvancedOptions(advancedOptions),
  };
};

export const multicastParametersToFormValues = (
  parameters: GraphPathLookupMulticastParameters,
): MulticastFormValues => {
  const commonValues = {
    group: parameters.group,
    receiver: parameters.receiver ?? '',
    securedPath: parameters.securedPath,
    source: parameters.source,
    vrf: isAutomaticFirstHop(parameters.firstHopAlgorithm)
      ? (parameters.firstHopAlgorithm.vrf ?? null)
      : null,
    fragmentOffset: parameters.fragmentOffset,
    firstHopAlgorithm: parameters.firstHopAlgorithm.type,
    entryPoints: isUserDefinedFirstHop(parameters.firstHopAlgorithm)
      ? entryPontsToEntryPointValues(parameters.firstHopAlgorithm.entryPoints)
      : [{ host: null, iface: null }],
  } as const;

  return {
    ...advancedOptionsToFormValues(parameters),
    ...commonValues,
  };
};
